import React from 'react';
import { motion } from 'framer-motion';
import { StreakEntry } from '../../components/StreakEntry';
import ProgressDashes from '../../components/ProgressDashes';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTopStreaks } from '../../utils/streakAnalytics';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

const TopStreaksPage = () => {
  const navigate = useNavigate();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const streaks = useMemo(() => getTopStreaks(transactions), [transactions]);
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    });
  };

  // Calculate maximum delay for all animations
  const maxAnimationDelay = useMemo(() => {
    const maxStreak = Math.max(...streaks.map(s => s.length));
    // Base delay (0.2s per streak) + streak bar animations (0.1s per bar) + buffer time
    return (streaks.length * 0.2) + (maxStreak * 0.1) + 1;
  }, [streaks]);

  // Auto-navigate after animations complete
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/insights/income-gap');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#221A51]/[0.07] relative flex flex-col">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      <div className="flex-1 px-4 sm:px-16 pt-20 pb-8 max-w-4xl mx-auto w-full flex flex-col">
        {/* Progress dashes */}
        <ProgressDashes currentStep={8} totalSteps={11} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-12 flex flex-col flex-1"
        >
          <h1 className="text-4xl font-bold text-[#221A51] mb-8">
            Top Streaks
          </h1>

          <div className="bg-white rounded-xl p-6 border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51] flex-1 flex flex-col">
            <div className="flex-1">
              {streaks.map((streak, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <StreakEntry
                    key={index}
                    title={streak.merchant}
                    period={`${formatDate(streak.startDate)} to ${formatDate(streak.endDate)}`}
                    days={streak.length}
                    streakCount={streak.length}
                    dashed={index === streaks.length - 1}
                    isLast={index === streaks.length - 1}
                    index={index}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Decorative elements */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="fixed bottom-0 right-0 w-48 h-48 opacity-20 -z-10"
      >
        <img src="/stars.svg" alt="" className="w-full h-full" />
      </motion.div>
    </div>
  );
};

export default TopStreaksPage;
